import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Communication Module Service Stores
 * Updated to match backend api-types
 */
export const communicationStores: ServiceStoreConfig[] = [
  {
    name: "channel",
    options: { enableStorage: true, enableErrorNotifications: true },
  },
  {
    name: "chat",
    options: { enableStorage: false, enableErrorNotifications: true },
  },
  {
    name: "chat-messages",
    options: { enableStorage: false, enableErrorNotifications: true },
  },
  {
    name: "comments",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.commentsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalComments = entities.length;
          const todayComments = entities.filter(
            (comment: Record<string, unknown>) => {
              const createdAt = new Date(comment.createdAt as string);
              const today = new Date();
              return createdAt.toDateString() === today.toDateString();
            }
          ).length;
          const thisWeekComments = entities.filter(
            (comment: Record<string, unknown>) => {
              const createdAt = new Date(comment.createdAt as string);
              const weekAgo = new Date();
              weekAgo.setDate(weekAgo.getDate() - 7);
              return createdAt > weekAgo;
            }
          ).length;
          const uniqueAuthors = new Set(
            entities.map((comment: Record<string, unknown>) => comment.authorId)
          ).size;

          return [
            {
              title: "Total Comments",
              value: () => totalComments.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todayComments.toString(),
              valueColor: "info.main",
            },
            {
              title: "This Week",
              value: () => thisWeekComments.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Contributors",
              value: () => uniqueAuthors.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "conversation-threads",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
    },
  },
  {
    name: "email-history",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.emailHistoryStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalEmails = entities.length;
          const sent = entities.filter(
            (email: Record<string, unknown>) => email.status === "sent"
          ).length;
          const failed = entities.filter(
            (email: Record<string, unknown>) => email.status === "failed"
          ).length;
          const thisWeek = entities.filter(
            (email: Record<string, unknown>) => {
              const sentDate = new Date(email.sentDate as string);
              const weekAgo = new Date();
              weekAgo.setDate(weekAgo.getDate() - 7);
              return sentDate > weekAgo;
            }
          ).length;

          return [
            {
              title: "Total Emails",
              value: () => totalEmails.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Sent",
              value: () => sent.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failed.toString(),
              valueColor: "error.main",
            },
            {
              title: "This Week",
              value: () => thisWeek.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "engineering-submittals",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.engineeringSubmittalsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalSubmittals = entities.length;
          const pending = entities.filter(
            (submittal: Record<string, unknown>) => submittal.status === "pending"
          ).length;
          const approved = entities.filter(
            (submittal: Record<string, unknown>) => submittal.status === "approved"
          ).length;
          const rejected = entities.filter(
            (submittal: Record<string, unknown>) => submittal.status === "rejected"
          ).length;

          return [
            {
              title: "Total Submittals",
              value: () => totalSubmittals.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Pending",
              value: () => pending.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Approved",
              value: () => approved.toString(),
              valueColor: "success.main",
            },
            {
              title: "Rejected",
              value: () => rejected.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "push-device-connection",
    options: { enableStorage: true, enableErrorNotifications: true },
  },
  {
    name: "push-notification",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.pushNotificationStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalNotifications = entities.length;
          const sent = entities.filter(
            (notification: Record<string, unknown>) => notification.status === "sent"
          ).length;
          const pending = entities.filter(
            (notification: Record<string, unknown>) => notification.status === "pending"
          ).length;
          const failed = entities.filter(
            (notification: Record<string, unknown>) => notification.status === "failed"
          ).length;

          return [
            {
              title: "Total Notifications",
              value: () => totalNotifications.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Sent",
              value: () => sent.toString(),
              valueColor: "success.main",
            },
            {
              title: "Pending",
              value: () => pending.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Failed",
              value: () => failed.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "push-trigger-send",
    options: { enableStorage: false, enableErrorNotifications: true },
  },
  {
    name: "rfi",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.rfiStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalRFIs = entities.length;
          const open = entities.filter(
            (rfi: Record<string, unknown>) => rfi.status === "open"
          ).length;
          const answered = entities.filter(
            (rfi: Record<string, unknown>) => rfi.status === "answered"
          ).length;
          const closed = entities.filter(
            (rfi: Record<string, unknown>) => rfi.status === "closed"
          ).length;

          return [
            {
              title: "Total RFIs",
              value: () => totalRFIs.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Open",
              value: () => open.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Answered",
              value: () => answered.toString(),
              valueColor: "success.main",
            },
            {
              title: "Closed",
              value: () => closed.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "rfi-attachments",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  {
    name: "submittal-documents",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  {
    name: "support-ticket",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.supportTicketStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalTickets = entities.length;
          const openTickets = entities.filter(
            (ticket: Record<string, unknown>) =>
              ticket.status === "open" || ticket.status === "in_progress"
          ).length;
          const resolvedTickets = entities.filter(
            (ticket: Record<string, unknown>) => ticket.status === "resolved"
          ).length;
          const highPriorityTickets = entities.filter(
            (ticket: Record<string, unknown>) => ticket.priority === "high" || ticket.priority === "critical"
          ).length;

          return [
            {
              title: "Total Tickets",
              value: () => totalTickets.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Open",
              value: () => openTickets.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Resolved",
              value: () => resolvedTickets.toString(),
              valueColor: "success.main",
            },
            {
              title: "High Priority",
              value: () => highPriorityTickets.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];
